import { api, apiUrl } from './api';
import { authedFetch } from '../hooks/useTelegram';
import { isResourceError } from './transfers';

/** Banana templates that issue Mastercard (everything else shows Visa). */
const MASTERCARD_TEMPLATE_IDS = new Set([14, 24, 27]);

export const isMastercardTemplate = (templateId: number | null | undefined) =>
  templateId != null && MASTERCARD_TEMPLATE_IDS.has(templateId);

export interface CardTemplate {
  templateId: number;
  name: string;
  country: string;
  /** ISO 3166-1 alpha-2 — issuer bank jurisdiction (flag in card settings). */
  countryIso?: string;
  type: string;
  paymentSystem: string;
  applyFee: number;
  rechargeFee: number;
  minDeposit: number;
  rechargeMin: number;
  rechargeMax: number;
  requiresDeposit: boolean;
  needsActivation: boolean;
  allowedPinLengths: string | null;
  limitPerTx: number;
  limitPerDay: number;
  limitPerMonth: number;
  priceUsd: number; // what the user pays to issue (per-template override or global fee)
  applePay: boolean;
  googlePay: boolean;
  /** Legacy OneClick VISA (#25) — usable in Ukraine; current #32 is not. */
  worksInUkraine: boolean;
  /** User must enter an email used for Banana registration (unused on current products). */
  requiresEmail: boolean;
  /** A pre-issued card can be claimed without registering a new Banana user. */
  poolAvailable: boolean;
}

import type { CardTreasury } from './cardPay';

export { cardPaySources, cardPayTo, type CardTreasury } from './cardPay';

export interface Card {
  id: string;
  providerCardId: string;
  templateId: number | null;
  name: string | null;
  label: string | null;
  last4: string | null;
  expiry: string | null;
  currency: string;
  status: string; // pending | active | frozen | closed | deleted
  /** Ops freeze. The cardholder cannot unfreeze this card. */
  adminHold?: boolean;
  needsActivation: boolean;
  pendingSecurementId: string | null;
  pendingSecurementAmount: string | null;
  pendingSecurementMethod: string | null; // 'OTP' | 'OOB' | 'VERIFICATION'
  pendingOtpValue: string | null; // 6-digit code when method=OTP or VERIFICATION
  createdAt: string;
}

export interface CardBalance {
  balance: number;
  refund: number | null;
  currency: string;
}

/** Highest balance first; pending / unknown sit at the end. Shared by home + cards tab. */
export function sortCardsByBalance(cards: Card[], balanceMap: Record<string, number>): Card[] {
  return [...cards].sort((a, b) => {
    const balOf = (c: Card) => (c.status === 'pending' ? -1 : (balanceMap[c.id] ?? -1));
    return balOf(b) - balOf(a);
  });
}

export interface CardSecret {
  number: string;
  cvv: string;
  expiry: string;
}

export type CardAccountStatus = 'none' | 'pending' | 'approved' | 'rejected';

/** Live registration status for a specific card template. */
export const cardAccount = (initData: string, templateId: number) =>
  api<{ registered: boolean; status: CardAccountStatus; rejectionMessage?: string | null }>(
    initData,
    `/v1/cards/account?template_id=${templateId}`,
  );

/** Ensure a Banana user exists for this template. Most templates auto-register
 *  with a generated email; templates with `requiresEmail` need `email`. */
export const ensureCardAccount = (
  initData: string,
  templateId: number,
  opts: { waitForApproval?: boolean; forceReregister?: boolean; email?: string } | boolean = {},
) => {
  // Back-compat: ensureCardAccount(init, id, true) → wait for approval.
  const options = typeof opts === 'boolean' ? { waitForApproval: opts } : opts;
  return api<{ registered: boolean; status: CardAccountStatus; rejectionMessage?: string | null }>(
    initData,
    '/v1/cards/account',
    {
      method: 'POST',
      body: JSON.stringify({
        template_id: templateId,
        ...(options.waitForApproval ? { wait_for_approval: true } : {}),
        ...(options.forceReregister ? { force_reregister: true } : {}),
        ...(options.email ? { email: options.email } : {}),
      }),
    },
  );
};

/** Pre-flight: is the user eligible to issue this template right now? Throws
 *  (with the error code in the message) when not — call it BEFORE charging. */
export const checkCardEligibility = (initData: string, templateId: number) =>
  api<{ ok: boolean }>(initData, `/v1/cards/eligibility?template_id=${templateId}`);

export const fetchTemplates = (initData: string) =>
  api<{ templates: CardTemplate[]; issueFeeUsd: number; treasury: CardTreasury }>(
    initData,
    '/v1/cards/templates',
  );

export const fetchCards = (initData: string) =>
  api<{
    cards: Card[];
    withdrawLimits?: {
      minAmountUsd: number;
      maxAmountUsd: number;
      maxPerHour?: number;
      remainingThisHour?: number;
    };
  }>(initData, '/v1/cards');

export const fetchCard = (initData: string, id: string) =>
  api<{ card: Card; balance: CardBalance | null }>(initData, `/v1/cards/${id}`);

/** Verify Banana's live card status immediately before an irreversible top-up payment. */
export const checkCardRechargeEligibility = (initData: string, id: string) =>
  api<{ ok: true; status: string }>(initData, `/v1/cards/${id}/recharge-eligibility`);

/** Canonical card-transaction row from the API (normalized live + stored). */
export interface CardTx {
  id: string;
  type: string;
  merchant: string | null;
  amount: string;
  currency: string;
  status: string;
  ts: number | null;
  /** Full amount debited from the card, including collected fees. */
  debitAmount?: string | null;
  /** Issuer bank fee included in the billing amount. */
  bankFeeUsd?: string | null;
  /** Cross-border/FX fee reported separately by Banana. */
  forexFeeUsd?: string | null;
  /** Bank/FX commission on merchant purchases (billing − check). */
  feeUsd?: string | null;
  /** That commission as a % of the merchant amount, e.g. "1.76". */
  feePct?: string | null;
  /** Original merchant charge in the local currency (e.g. "100.00"), when different. */
  localAmount?: string | null;
  /** Local currency code for `localAmount`, e.g. "PLN". */
  localCurrency?: string | null;
}

export const fetchCardTransactions = (initData: string, id: string) =>
  api<{ transactions: CardTx[] }>(initData, `/v1/cards/${id}/transactions`);

const reauthHeaders = (token?: string) => (token ? { 'X-Reauth': token } : undefined);

export const issueCard = (
  initData: string,
  body: {
    template_id: number;
    deposit_amount?: number;
    /** Omitted only when a promo code makes the issue free. */
    payment_transfer_id?: string;
    promo_code?: string;
  },
  idempotencyKey: string,
  reauthToken?: string,
) =>
  api<{ card: Card | null; status: string }>(initData, '/v1/cards', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey, ...reauthHeaders(reauthToken) },
    body: JSON.stringify(body),
  });

/**
 * Pull the provider's human-readable message out of a thrown card error. The
 * `api()` helper embeds the whole error body as JSON in the thrown message, e.g.
 * `API 400 /v1/cards/account: {"error":"provider_error","status":400,
 * "detail":{"detail":"Phone number ... is already registered ..."}}`. We surface
 * that message only for genuinely user-facing validation strings — phone already
 * registered, invalid format, etc. Messages that contain internal identifiers
 * (template_id, card_id, user_id, numeric IDs) are suppressed and the generic
 * "provider busy" fallback is shown instead.
 */
function providerErrorMessage(raw: string): string | null {
  const i = raw.indexOf('{');
  if (i === -1) return null;
  let parsed: { status?: number; detail?: unknown };
  try {
    parsed = JSON.parse(raw.slice(i));
  } catch {
    return null;
  }
  const status = parsed.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) return null;
  const d = parsed.detail;
  const msg =
    typeof d === 'string'
      ? d
      : d && typeof d === 'object'
        ? ((d as Record<string, unknown>).detail ??
          (d as Record<string, unknown>).message ??
          (d as Record<string, unknown>).error)
        : null;
  if (typeof msg !== 'string' || !msg.trim()) return null;
  // Suppress messages that expose internal identifiers — show generic fallback.
  if (/template_id|card_id|user_id|\bid\s*=\s*\d|\bid:\s*\d/i.test(msg)) return null;
  return msg.trim();
}

export type CardErrorContext = 'generic' | 'issue' | 'topUp' | 'withdraw';

function retryAfterSeconds(raw: string): number | null {
  const match = raw.match(/"retryAfter"\s*:\s*(\d+)/i);
  if (!match) return null;
  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}

/** Map a thrown card error to a friendly message. */
export function cardErrorMessage(
  raw: string,
  t: (k: string, o?: Record<string, unknown>) => string,
  fallback: string,
  context: CardErrorContext = 'generic',
): string {
  const has = (code: string) => raw.includes(code);
  // A mined-but-reverted card payment moved no USDT. Explain that explicitly;
  // the generic energy message makes it sound as if the user's funds were spent.
  if (has('onchain_reverted')) return t('cards.errPaymentReverted');
  if (
    has('resources_not_on_chain') ||
    has('energy_rental_failed') ||
    has('sponsored_resources_expired') ||
    has('rental_result_uncertain') ||
    has('preparation_lease_expired') ||
    has('transfer_preparing')
  ) {
    return t('cards.errEnergyPreparing');
  }
  if (has('insufficient_bandwidth') || has('bandwidth_rental_failed')) {
    return t('cards.errBandwidth');
  }
  // A Tron energy/bandwidth shortfall at broadcast reads as "...insufficient..." —
  // check it BEFORE the generic balance branch so we don't mislabel it as
  // "insufficient balance" (the user's wallet is fine; the network is short on energy).
  if (isResourceError(raw)) return t('cards.errEnergy');
  if (has('energy_estimation_failed') || has('tron_energy_estimation_failed')) {
    return t('cards.errEnergy');
  }
  if (
    has('cooldown') ||
    has('daily_limit_reached') ||
    has('sponsor_risk_limit') ||
    has('transfer_in_progress')
  ) {
    return t('cards.errCooldown');
  }
  if (has('card_fee_not_configured') || has('cards_unavailable')) return t('cards.errUnavailable');
  if (has('payment_too_low')) return t('cards.errPaymentLow');
  if (has('amount_below_min') || has('below_min')) return t('cards.errWithdrawMin');
  if (has('above_max')) return t('cards.errWithdrawMax');
  if (has('rate_limited')) {
    // Banana can put "rate_limited" inside a provider_error response. That is
    // provider congestion, not our withdrawal limit.
    if (has('"error":"provider_error"')) return t('cards.errProviderRateLimit');
    const seconds = retryAfterSeconds(raw);
    if (seconds) {
      const key =
        context === 'issue'
          ? 'cards.errIssueRateLimit'
          : context === 'topUp'
            ? 'cards.errTopUpRateLimit'
            : context === 'withdraw'
              ? 'cards.errWithdrawRequestRate'
              : 'cards.errRequestRateLimit';
      return t(key, { seconds });
    }
    return context === 'withdraw'
      ? t('cards.errWithdrawRate')
      : t('cards.errRequestRateLimit', { seconds: 60 });
  }
  if (has('invalid_address')) return t('cards.errWithdrawAddress');
  if (has('code_used')) return t('promo.errUsed');
  if (
    has('code_invalid') ||
    has('wrong_kind') ||
    has('invalid_card_issue_claim') ||
    has('promo_payment_mismatch') ||
    has('promo_payment_already_bound')
  )
    return t('promo.errInvalid');
  if (has('issue_in_progress')) return t('cards.errIssueInProgress');
  if (has('issue_terms_mismatch')) return t('cards.errIssueTermsChanged');
  if (has('payment_already_used')) return t('cards.errPaymentUsed');
  if (has('payment_')) return t('cards.errPayment');
  if (has('card_limit_reached')) return t('cards.errLimit');
  if (has('card_not_active')) return t('cards.topUpFailed');
  if (has('template_mismatch')) return t('cards.errTemplateMismatch');
  if (has('already_has_template')) return t('cards.errAlreadyHave');
  if (has('not_approved')) return t('cards.errNotApproved');
  if (has('register_required')) return t('cards.errRegister');
  if (has('email_required')) return t('cards.emailInvalid');
  if (has('template_not_offered') || has('template_not_found')) return t('cards.errTemplate');
  if (has('seed_unavailable')) return t('coin.seedUnavailable');
  // Bank float empty after payment — ops credits manually; tell the user to wait.
  if (has('provider_float_empty')) return t('cards.topUpInProgress');
  // Show the provider's own message for actionable validation errors (e.g.
  // "phone already registered"); otherwise the generic "provider busy" string.
  // Must run BEFORE the generic "insufficient" branch — Banana's
  // "Insufficient funds on merchant balance" would otherwise look like the
  // user's wallet is empty.
  if (has('provider_error')) return providerErrorMessage(raw) ?? t('cards.errProvider');
  if (has('insufficient')) return t('send.errInsufficient');
  // Catch-all: any message leaking internal field names → generic error.
  if (/template_id|card_id|user_id/i.test(raw)) return t('cards.errProvider');
  return fallback;
}

export type CardIntentStatus = 'pending' | 'processing' | 'success' | 'failed' | 'needs_manual';

export interface CardIntent {
  id: string;
  kind: string;
  status: CardIntentStatus;
  cardId: string | null;
  amount: number | null;
  createdAt: string;
  /** Worker stage from `result.stage` (e.g. banana_ok, payout_queued). */
  stage?: string | null;
}

export type RechargeResult = {
  ok: boolean;
  intentId: string;
  status: CardIntentStatus;
  cardId?: string | null;
};

export const rechargeCard = (
  initData: string,
  id: string,
  body: { amount: number; payment_transfer_id: string },
  idempotencyKey: string,
  reauthToken?: string,
) =>
  api<RechargeResult>(initData, `/v1/cards/${id}/recharge`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey, ...reauthHeaders(reauthToken) },
    body: JSON.stringify(body),
  });

export const fetchCardIntents = (
  initData: string,
  opts: { kind?: string; status?: CardIntentStatus | 'open' } = {},
) => {
  const q = new URLSearchParams();
  if (opts.kind) q.set('kind', opts.kind);
  if (opts.status) q.set('status', opts.status);
  const qs = q.toString();
  return api<{ intents: CardIntent[] }>(initData, `/v1/cards/intents${qs ? `?${qs}` : ''}`);
};

export const fetchCardIntent = (initData: string, intentId: string) =>
  api<{ intent: CardIntent }>(initData, `/v1/cards/intents/${intentId}`);

export interface RecoverableCardIssue {
  id: string;
  status: 'pending' | 'processing' | 'failed' | 'success';
  templateId: number;
  depositAmount: number;
  baseFeeUsd: number;
  discountPercent: number;
  requiredUsd: number;
  paymentTransferId: string | null;
  promoCode: string | null;
  processingRetryAt: string | null;
}

export const fetchRecoverableCardIssue = (initData: string) =>
  api<{ intent: RecoverableCardIssue | null }>(initData, '/v1/cards/issue-recovery');

export const renameCard = (initData: string, id: string, label: string | null) =>
  api<{ card: Card }>(initData, `/v1/cards/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });

export const withdrawCard = (
  initData: string,
  id: string,
  body: { amount: number; address: string; chain: 'trx'; token: 'USDT' },
  idempotencyKey: string,
  reauthToken?: string,
) =>
  api<{ ok: boolean; intentId: string; status: CardIntentStatus }>(
    initData,
    `/v1/cards/${id}/withdraw`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey, ...reauthHeaders(reauthToken) },
      body: JSON.stringify(body),
    },
  );

/** Sensitive PAN/CVV — needs a reauth token; raw fetch so callers can read 401. */
export async function cardDetails(initData: string, id: string, reauthToken?: string) {
  const res = await authedFetch(initData, apiUrl(`/v1/cards/${id}/details`), {
    method: 'POST',
    headers: reauthHeaders(reauthToken),
  });
  if (res.status === 401) return { reauth: true as const };
  if (!res.ok) throw new Error(`details ${res.status}`);
  return (await res.json()) as CardSecret;
}

export const setCardStatus = (
  initData: string,
  id: string,
  alter: 'LOCK' | 'UNLOCK' | 'CLOSE',
  reauthToken?: string,
) =>
  api<{ ok: boolean; status: string }>(initData, `/v1/cards/${id}/status`, {
    method: 'POST',
    headers: reauthHeaders(reauthToken),
    body: JSON.stringify({ alter }),
  });

export const setCardPin = (initData: string, id: string, pin: string, reauthToken?: string) =>
  api<{ ok: boolean }>(initData, `/v1/cards/${id}/pin`, {
    method: 'POST',
    headers: reauthHeaders(reauthToken),
    body: JSON.stringify({ pin }),
  });

export const confirm3ds = (
  initData: string,
  id: string,
  securement_id: string | number,
  action: 'ACCEPTED' | 'DECLINED',
  reauthToken?: string,
) =>
  api<{ ok: boolean }>(initData, `/v1/cards/${id}/3ds`, {
    method: 'POST',
    headers: reauthHeaders(reauthToken),
    body: JSON.stringify({ securement_id, action }),
  });

/** Clear a shown OTP / verification code (OTP / VERIFICATION methods have no
 *  provider confirm — this just hides the on-screen prompt). */
export const dismiss3ds = (initData: string, id: string) =>
  api<{ ok: boolean }>(initData, `/v1/cards/${id}/3ds/dismiss`, { method: 'POST' });
